"use client"

import Image from "next/image"
import { ComponentProps, RefObject } from "react"
import { Desktop, Mobile } from "./MediaQueries"

const VIDEO_EXTENSIONS = new Set(["mp4", "webm", "mov"])

export type MediaRendererProps = {
  mediaUrl?: string
  alt: string
  priority?: boolean
  variant?: "desktop" | "mobile"
  videoRef?: RefObject<HTMLVideoElement>
  videoProps?: ComponentProps<"video">
}

export const getExtension = (mediaUrl?: string) =>
  mediaUrl?.split("?")?.[0]?.split(".")?.pop()?.toLowerCase() || ""

export const isVideo = (mediaUrl?: string) => VIDEO_EXTENSIONS.has(getExtension(mediaUrl))

export const getVideoType = (extension: string) => {
  if (extension === "mov") {
    return "video/quicktime"
  }
  return `video/${extension || "mp4"}`
}

const renderMediaElement = ({
  mediaUrl,
  alt,
  priority,
  variant,
  videoRef,
  videoProps,
}: MediaRendererProps) => {
  if (!mediaUrl) return null
  const extension = getExtension(mediaUrl)

  if (isVideo(mediaUrl)) {
    return (
      <video
        key={`${variant ?? mediaUrl}-video`}
        autoPlay
        muted
        loop
        playsInline
        aria-label={`${alt} video`}
        ref={videoRef}
        {...videoProps}
      >
        <source src={mediaUrl} type={getVideoType(extension)} />
      </video>
    )
  }

  return (
    <Image
      key={`${variant ?? mediaUrl}-image`}
      src={mediaUrl}
      fill
      priority={priority}
      alt={alt}
      sizes="100vw"
    />
  )
}

type ResponsiveMediaProps = {
  desktopMedia?: string
  mobileMedia?: string
  alt: string
  priority?: boolean
  desktopVideoRef?: RefObject<HTMLVideoElement>
  mobileVideoRef?: RefObject<HTMLVideoElement>
  videoProps?: ComponentProps<"video">
}

export const ResponsiveMedia = ({
  desktopMedia,
  mobileMedia,
  alt,
  priority,
  desktopVideoRef,
  mobileVideoRef,
  videoProps,
}: ResponsiveMediaProps) => (
  <>
    {desktopMedia && (
      <Desktop>
        {renderMediaElement({
          mediaUrl: desktopMedia,
          alt,
          priority,
          variant: "desktop",
          videoRef: desktopVideoRef,
          videoProps,
        })}
      </Desktop>
    )}
    {mobileMedia && (
      <Mobile>
        {renderMediaElement({
          mediaUrl: mobileMedia,
          alt,
          priority,
          variant: "mobile",
          videoRef: mobileVideoRef,
          videoProps,
        })}
      </Mobile>
    )}
  </>
)

export const MediaRenderer = (props: MediaRendererProps) => renderMediaElement(props)
